// Home shell for the five sidebar/tab sections: Trips (list + new-trip card),
// Discover (static inspiration content), World Map (scratch map in
// WorldMapView), Travel Inspiration (localStorage-persisted in BucketView), and You
// (default packing list editor + "coming soon" placeholder cards).
import React from 'react';
import { PALETTE, headerPad } from '../theme';
import { Icon, IconBtn, SectionTitle, Tag, Patch, TabBar } from '../components/UI';
import { INSPIRATION, BUCKET } from '../data';
import { daysUntil, loadDefaultPacking, saveDefaultPacking, DEFAULT_PACKING, fmtDMY, sortByDateStart } from '../utils/trips';
import { isPastTrip } from '../utils/visited';
import { WorldMapView } from './WorldMapView';

export function HomeScreen({ trips = [], isMobile, activeTab, onTabChange, onNewTrip, onOpenTrip, onDeleteTrip }) {
  const headerTitle =
    activeTab === 'home'     ? 'Where to, friend?' :
    activeTab === 'discover' ? 'Discover' :
    activeTab === 'map'      ? 'World Map' :
    activeTab === 'bucket'   ? 'Travel Inspiration' : 'You';

  const topPad = headerPad(isMobile);

  return (
    <div style={{ background: PALETTE.paper, minHeight: '100vh' }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 4,
        background: PALETTE.paper,
        padding: topPad,
        display: 'flex', flexDirection: 'column', gap: 4,
        borderBottom: `0.5px solid ${PALETTE.ruleSoft}`,
      }}>
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 36 }}>
            <IconBtn name="search" label="Search" />
            <div style={{
              fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
              color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase',
            }}>{`${new Date().toLocaleDateString('en-GB', { weekday: 'short' })} · ${new Date().toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}`}</div>
            <IconBtn name="more" label="More options" />
          </div>
        )}
        <div style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: isMobile ? 32 : 28,
          fontWeight: 400,
          letterSpacing: -0.8, color: PALETTE.ink, lineHeight: 1.05,
          padding: '4px 0 0',
        }}>{headerTitle}</div>
      </div>

      <div style={{ paddingBottom: isMobile ? 100 : 40 }}>
        {activeTab === 'home'     && <TripsView trips={trips} onNewTrip={onNewTrip} onOpenTrip={onOpenTrip} onDeleteTrip={onDeleteTrip} isMobile={isMobile} />}
        {activeTab === 'discover' && <DiscoverView isMobile={isMobile} />}
        {activeTab === 'map'      && <WorldMapView isMobile={isMobile} trips={trips} />}
        {activeTab === 'bucket'   && <BucketView isMobile={isMobile} />}
        {activeTab === 'profile'  && <ProfileView isMobile={isMobile} />}
      </div>

      {isMobile && <TabBar active={activeTab} onChange={onTabChange} />}
    </div>
  );
}

function TripsView({ trips, onNewTrip, onOpenTrip, onDeleteTrip, isMobile }) {
  const px = isMobile ? 20 : 28;
  const upcoming = sortByDateStart(trips.filter(t => !isPastTrip(t)));
  const past = trips.filter(t => isPastTrip(t));

  return (
    <>
      <div style={{ padding: `20px ${px}px 0` }}>
        <button onClick={onNewTrip} style={{
          appearance: 'none', cursor: 'pointer',
          width: '100%', padding: '18px',
          background: PALETTE.card,
          border: `1px dashed ${PALETTE.rule}`,
          borderRadius: 14,
          display: 'flex', alignItems: 'center', gap: 14,
          color: PALETTE.ink,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: PALETTE.accent, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="plus" size={20} stroke={2} />
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 18, fontWeight: 400, letterSpacing: -0.3 }}>
              Plan a new trip
            </div>
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, marginTop: 2 }}>
              Destination & dates → ideas → itinerary
            </div>
          </div>
        </button>
      </div>

      {upcoming.length === 0 && past.length === 0 ? (
        <div style={{ padding: `36px ${px}px`, textAlign: 'center' }}>
          <div style={{ fontFamily: 'Fraunces', fontSize: 18, fontStyle: 'italic', color: PALETTE.inkMute, lineHeight: 1.6 }}>
            No trips yet.<br />Plan your first adventure above.
          </div>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <>
              <div style={{ height: 28 }} />
              <SectionTitle kicker={`Upcoming · ${upcoming.length}`}>Your trips</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: `0 ${px}px` }}>
                {upcoming.map(t => (
                  <TripCard key={t.id} trip={t} onOpen={() => onOpenTrip(t.id)} onDelete={() => onDeleteTrip(t.id)} />
                ))}
              </div>
            </>
          )}

          {past.length > 0 && (
            <>
              <div style={{ height: 36 }} />
              <SectionTitle kicker={`Past · ${past.length}`}>Memories</SectionTitle>
              <div style={{ display: 'flex', gap: 12, padding: `0 ${px}px`, overflowX: 'auto', paddingBottom: 4 }}>
                {past.map(p => (
                  <button key={p.id} onClick={() => onOpenTrip(p.id)} style={{
                    appearance: 'none', border: 0, cursor: 'pointer',
                    flexShrink: 0, width: 140, background: 'transparent', textAlign: 'left', padding: 0,
                  }}>
                    <Patch swatch={p.swatch} height={170} style={{ borderRadius: 8 }} />
                    <div style={{ fontFamily: 'Fraunces', fontSize: 16, marginTop: 8, color: PALETTE.ink }}>{p.title || p.destination}</div>
                    <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginTop: 2 }}>
                      {fmtDMY(p.dateStart)}{p.dateEnd ? ` – ${fmtDMY(p.dateEnd)}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

function TripCard({ trip, onOpen, onDelete }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const menuRef = React.useRef(null);
  const cd = daysUntil(trip.dateStart);

  React.useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) { setMenuOpen(false); setConfirmDelete(false); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={onOpen} style={{
        appearance: 'none', cursor: 'pointer',
        background: PALETTE.card, borderRadius: 14, padding: 12,
        display: 'flex', gap: 14, alignItems: 'stretch', textAlign: 'left',
        border: `0.5px solid ${PALETTE.ruleSoft}`, width: '100%',
        transition: 'box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(34,31,26,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        <Patch swatch={trip.swatch} height={72} style={{ width: 88, flexShrink: 0, borderRadius: 8 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '2px 4px', paddingRight: 32 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 22, fontWeight: 400, letterSpacing: -0.4, color: PALETTE.ink, lineHeight: 1.1 }}>
                {trip.title || trip.destination?.split(',')[0]}
              </div>
              {cd != null && (
                <div style={{
                  fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: cd <= 7 ? PALETTE.accent : PALETTE.inkMute,
                  letterSpacing: 0.5, flexShrink: 0,
                  background: cd <= 7 ? 'rgba(184,82,48,0.1)' : PALETTE.paperDim,
                  padding: '2px 7px', borderRadius: 4,
                }}>
                  {cd === 0 ? 'Today' : `${cd}d away`}
                </div>
              )}
            </div>
            <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, marginTop: 3 }}>
              {trip.legs && trip.legs.length > 1
                ? trip.legs.map(l => (l.destination || '').split(',')[0].trim()).filter(Boolean).join(' → ')
                : trip.destination}
              {trip.dateStart ? ` · ${fmtDMY(trip.dateStart)}${trip.dateEnd ? ` – ${fmtDMY(trip.dateEnd)}` : ''}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <Tag tone={trip.status === 'planning' ? 'accent' : 'default'}>{trip.status || 'planning'}</Tag>
            {trip.dayCount && <Tag>{trip.dayCount} days</Tag>}
          </div>
        </div>
      </button>

      {/* Three-dot menu trigger */}
      <button
        onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); setConfirmDelete(false); }}
        aria-label="Trip options"
        style={{
          position: 'absolute', top: 10, right: 10,
          appearance: 'none', border: 0, cursor: 'pointer',
          width: 28, height: 28, borderRadius: 8,
          background: menuOpen ? PALETTE.paperDim : 'transparent',
          color: PALETTE.inkMute,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (!menuOpen) e.currentTarget.style.background = PALETTE.paperDim; }}
        onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.background = 'transparent'; }}
      >
        <Icon name="more" size={16} />
      </button>

      {/* Dropdown menu */}
      {menuOpen && (
        <div ref={menuRef} style={{
          position: 'absolute', top: 40, right: 8, zIndex: 20,
          background: PALETTE.card, borderRadius: 12,
          border: `1px solid ${PALETTE.rule}`,
          boxShadow: '0 6px 20px rgba(34,31,26,0.14)',
          padding: 6, minWidth: 160,
        }}>
          <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onOpen(); }} style={{
            appearance: 'none', border: 0, cursor: 'pointer', width: '100%', textAlign: 'left',
            padding: '9px 12px', borderRadius: 8, background: 'transparent',
            fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink,
            display: 'flex', alignItems: 'center', gap: 8,
          }}
          onMouseEnter={e => e.currentTarget.style.background = PALETTE.paperDim}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Icon name="list" size={14} /> Open itinerary
          </button>

          <div style={{ height: 1, background: PALETTE.ruleSoft, margin: '4px 0' }} />

          {!confirmDelete ? (
            <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }} style={{
              appearance: 'none', border: 0, cursor: 'pointer', width: '100%', textAlign: 'left',
              padding: '9px 12px', borderRadius: 8, background: 'transparent',
              fontFamily: 'Inter', fontSize: 13, color: PALETTE.red,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
            onMouseEnter={e => e.currentTarget.style.background = PALETTE.redSoft}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Icon name="x" size={14} /> Delete trip
            </button>
          ) : (
            <div style={{ padding: '8px 12px' }}>
              <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, marginBottom: 8 }}>
                Delete "{trip.title || trip.destination?.split(',')[0]}"?
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={(e) => { e.stopPropagation(); onDelete(); setMenuOpen(false); }} style={{
                  appearance: 'none', border: 0, cursor: 'pointer', flex: 1,
                  padding: '6px 0', background: PALETTE.red, color: '#fff',
                  borderRadius: 6, fontFamily: 'Inter', fontSize: 12, fontWeight: 600,
                }}>Delete</button>
                <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }} style={{
                  appearance: 'none', border: 0, cursor: 'pointer', flex: 1,
                  padding: '6px 0', background: PALETTE.paperDim, color: PALETTE.inkSoft,
                  borderRadius: 6, fontFamily: 'Inter', fontSize: 12,
                }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DiscoverView({ isMobile }) {
  const px = isMobile ? 20 : 28;
  return (
    <>
      <div style={{ padding: `20px ${px}px 0` }}>
        <div style={{ fontFamily: 'Fraunces', fontSize: 16, fontStyle: 'italic', color: PALETTE.inkSoft, lineHeight: 1.5 }}>
          A few trips, shows, and fares worth dreaming about — fresh inspiration for the horizon.
        </div>
      </div>
      <div style={{ height: 24 }} />
      <SectionTitle kicker="Curated">For you</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: `0 ${px}px` }}>
        {INSPIRATION.map((it, i) => (
          <div key={i} style={{ background: PALETTE.card, borderRadius: 14, overflow: 'hidden', border: `0.5px solid ${PALETTE.ruleSoft}` }}>
            <Patch swatch={it.swatch} height={140} badge={it.badge} style={{ borderRadius: 0 }} />
            <div style={{ padding: 14 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
                {it.kind === 'preset' ? 'Itinerary' : it.kind === 'concert' ? 'Live' : 'Flight deal'} · {it.meta}
              </div>
              <div style={{ fontFamily: 'Fraunces', fontSize: 20, letterSpacing: -0.3, color: PALETTE.ink }}>{it.title}</div>
              <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, marginTop: 4, lineHeight: 1.4 }}>{it.body}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// Exported for ShareInboxScreen — sharing "to Travel Inspiration" writes
// into this same localStorage-persisted list (there's no server-side bucket
// collection, unlike trips), so it must use the exact same key/shape.
export const BUCKET_KEY = 'tp_bucket_v1';
export const loadBucket = () => {
  try { const s = localStorage.getItem(BUCKET_KEY); return s ? JSON.parse(s) : BUCKET; }
  catch { return BUCKET; }
};
export const saveBucket = (items) => localStorage.setItem(BUCKET_KEY, JSON.stringify(items));

const WHEN_OPTIONS = ['someday', 'next', '2026', '2027', '2028', 'spring', 'summer', 'autumn', 'winter'];

function IgImportSheet({ onAdd, onClose }) {
  const [url, setUrl] = React.useState('');
  const [status, setStatus] = React.useState('idle'); // idle|loading|done|error
  const [extracted, setExtracted] = React.useState([]);
  const [selected, setSelected] = React.useState(new Set());
  const [errMsg, setErrMsg] = React.useState('');

  const extract = async () => {
    const trimmed = url.trim();
    if (!trimmed.includes('instagram.com')) { setErrMsg('Please paste a valid instagram.com URL'); return; }
    setStatus('loading'); setErrMsg('');
    try {
      const r = await fetch('/api/tp/ig/extract', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Extraction failed');
      if (!d.items?.length) { setStatus('done'); setExtracted([]); return; }
      setExtracted(d.items.map(it => ({ ...it, sourceUrl: trimmed })));
      setSelected(new Set(d.items.map((_, i) => i)));
      setStatus('done');
    } catch (err) { setErrMsg(err.message); setStatus('error'); }
  };

  const toggleSelect = (i) => setSelected(s => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });

  const handleAdd = () => {
    const toAdd = extracted.filter((_, i) => selected.has(i)).map(it => ({
      title: it.title,
      tag: 'someday',
      destination: it.destination || '',
      notes: `${it.notes ? it.notes + ' · ' : ''}Source: ${it.sourceUrl}`,
    }));
    onAdd(toAdd);
    onClose();
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(34,31,26,0.45)', display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 680, margin: '0 auto',
        background: PALETTE.paper, borderTopLeftRadius: 24, borderTopRightRadius: 24,
        padding: '12px 0 36px', display: 'flex', flexDirection: 'column', maxHeight: '85vh',
      }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: PALETTE.rule, margin: '0 auto 16px' }} />
        <div style={{ padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
          <div>
            <div style={{ fontFamily: 'Fraunces', fontSize: 22, letterSpacing: -0.4, color: PALETTE.ink }}>Import from Instagram</div>
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, marginTop: 3 }}>Paste a public post URL — we'll extract the places</div>
          </div>
          <button onClick={onClose} style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        </div>

        <div style={{ padding: '0 24px', display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            autoFocus
            value={url}
            onChange={e => { setUrl(e.target.value); setStatus('idle'); setErrMsg(''); }}
            onKeyDown={e => e.key === 'Enter' && extract()}
            placeholder="https://www.instagram.com/p/..."
            style={{
              flex: 1, appearance: 'none', border: `1px solid ${errMsg ? PALETTE.red : PALETTE.rule}`,
              borderRadius: 10, padding: '11px 14px', background: PALETTE.card,
              fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, outline: 'none',
            }}
          />
          <button onClick={extract} disabled={status === 'loading'} style={{
            appearance: 'none', border: 0, cursor: status === 'loading' ? 'default' : 'pointer',
            background: PALETTE.accent, color: '#fff', borderRadius: 10,
            padding: '11px 18px', fontFamily: 'Inter', fontSize: 13, fontWeight: 600,
            opacity: status === 'loading' ? 0.6 : 1, flexShrink: 0,
          }}>
            {status === 'loading' ? '…' : 'Extract'}
          </button>
        </div>

        {errMsg && (
          <div style={{ padding: '0 24px 12px', fontFamily: 'Inter', fontSize: 12, color: PALETTE.red }}>{errMsg}</div>
        )}

        {status === 'loading' && (
          <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${PALETTE.accent}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft }}>Fetching & analysing post…</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {status === 'done' && extracted.length === 0 && (
          <div style={{ padding: '20px 24px', fontFamily: 'Fraunces', fontSize: 16, fontStyle: 'italic', color: PALETTE.inkMute }}>
            No travel places found in this post. Try a different one.
          </div>
        )}

        {status === 'done' && extracted.length > 0 && (
          <div style={{ overflowY: 'auto', flex: 1, padding: '0 24px' }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 10 }}>
              {extracted.length} place{extracted.length !== 1 ? 's' : ''} found · tap to select
            </div>
            {extracted.map((it, i) => (
              <button key={i} onClick={() => toggleSelect(i)} style={{
                appearance: 'none', border: `1.5px solid ${selected.has(i) ? PALETTE.accent : PALETTE.rule}`,
                borderRadius: 12, padding: '12px 14px', width: '100%', textAlign: 'left',
                cursor: 'pointer', background: selected.has(i) ? `${PALETTE.accent}0F` : PALETTE.card,
                marginBottom: 8, display: 'flex', gap: 12, alignItems: 'flex-start',
                transition: 'all 0.15s',
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 1,
                  border: `1.5px solid ${selected.has(i) ? PALETTE.accent : PALETTE.rule}`,
                  background: selected.has(i) ? PALETTE.accent : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {selected.has(i) && <Icon name="check" size={11} stroke={2.5} style={{ color: '#fff' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'Fraunces', fontSize: 17, color: PALETTE.ink, letterSpacing: -0.2 }}>{it.title}</div>
                  {it.destination && (
                    <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginTop: 3 }}>
                      {it.category} · {it.destination}
                    </div>
                  )}
                  {it.notes && (
                    <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, marginTop: 4, lineHeight: 1.4 }}>{it.notes}</div>
                  )}
                </div>
              </button>
            ))}

            <button
              onClick={handleAdd}
              disabled={selected.size === 0}
              style={{
                appearance: 'none', border: 0, cursor: selected.size === 0 ? 'default' : 'pointer',
                width: '100%', padding: '14px', marginTop: 8,
                background: selected.size === 0 ? PALETTE.rule : PALETTE.ink,
                color: selected.size === 0 ? PALETTE.inkMute : PALETTE.paper,
                borderRadius: 12, fontFamily: 'Inter', fontSize: 14, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.15s',
              }}
            >
              <Icon name="plus" size={16} /> Add {selected.size} to Travel Inspiration
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function BucketView({ isMobile }) {
  const px = isMobile ? 20 : 28;
  const [items, setItems] = React.useState(() => loadBucket());
  const [adding, setAdding] = React.useState(false);
  const [newText, setNewText] = React.useState('');
  const [newWhen, setNewWhen] = React.useState('someday');
  const [editingIdx, setEditingIdx] = React.useState(null);
  const [editText, setEditText] = React.useState('');
  const [editWhen, setEditWhen] = React.useState('someday');
  const [showIgImport, setShowIgImport] = React.useState(false);

  const persist = (newItems) => { setItems(newItems); saveBucket(newItems); };

  const addItem = () => {
    if (!newText.trim()) return;
    persist([...items, { title: newText.trim(), tag: newWhen }]);
    setNewText(''); setNewWhen('someday'); setAdding(false);
  };

  const deleteItem = (idx) => persist(items.filter((_, i) => i !== idx));

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditText(items[idx].title);
    setEditWhen(items[idx].tag || 'someday');
  };

  const saveEdit = () => {
    if (!editText.trim()) return;
    persist(items.map((b, i) => i === editingIdx ? { ...b, title: editText.trim(), tag: editWhen } : b));
    setEditingIdx(null); setEditText(''); setEditWhen('someday');
  };

  const handleIgAdd = (newEntries) => persist([...items, ...newEntries]);

  return (
    <>
      <div style={{ padding: `20px ${px}px 0` }}>
        <div style={{ fontFamily: 'Fraunces', fontSize: 16, fontStyle: 'italic', color: PALETTE.inkSoft, lineHeight: 1.5 }}>
          The list of someday. Pin a year, or leave it open.
        </div>
      </div>
      <div style={{ height: 16 }} />
      <div style={{ padding: `0 ${px}px`, marginBottom: 8 }}>
        <button onClick={() => setShowIgImport(true)} style={{
          appearance: 'none', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 14px', borderRadius: 999,
          background: 'transparent', color: PALETTE.inkSoft,
          border: `1px solid ${PALETTE.rule}`,
          fontFamily: 'Inter', fontSize: 12, fontWeight: 500,
          transition: 'all 0.15s',
        }}>
          <Icon name="sparkle" size={14} /> Import from Instagram
        </button>
      </div>
      <div style={{ height: 8 }} />
      <div style={{ padding: `0 ${px}px`, display: 'flex', flexDirection: 'column' }}>
        {items.map((b, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 14,
            padding: '14px 0',
            borderBottom: i < items.length - 1 ? `0.5px solid ${PALETTE.rule}` : 'none',
          }}>
            <div style={{ fontFamily: 'Fraunces', fontSize: 22, color: PALETTE.inkMute, fontWeight: 300, width: 28, paddingTop: 2, flexShrink: 0 }}>
              {String(i + 1).padStart(2, '0')}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingIdx === i ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    autoFocus
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingIdx(null); }}
                    placeholder="Destination or experience"
                    style={{
                      appearance: 'none', border: `1px solid ${PALETTE.rule}`,
                      borderRadius: 8, padding: '8px 10px', background: PALETTE.card,
                      fontFamily: 'Fraunces', fontSize: 17, color: PALETTE.ink, outline: 'none',
                      width: '100%', boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>When</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {WHEN_OPTIONS.map(w => (
                        <button key={w} onClick={() => setEditWhen(w)} style={{
                          appearance: 'none', cursor: 'pointer',
                          padding: '4px 10px', borderRadius: 999,
                          background: editWhen === w ? PALETTE.ink : 'transparent',
                          color: editWhen === w ? PALETTE.paper : PALETTE.inkSoft,
                          fontFamily: 'Inter', fontSize: 11, fontWeight: 500,
                          border: editWhen === w ? '1px solid transparent' : `1px solid ${PALETTE.rule}`,
                          transition: 'all 0.12s',
                        }}>{w}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={saveEdit} style={{
                      appearance: 'none', border: 0, cursor: 'pointer',
                      background: PALETTE.ink, color: PALETTE.paper,
                      borderRadius: 8, padding: '8px 16px',
                      fontFamily: 'Inter', fontSize: 12, fontWeight: 600,
                    }}>Save</button>
                    <button onClick={() => setEditingIdx(null)} style={{
                      appearance: 'none', border: 0, cursor: 'pointer',
                      background: 'transparent', color: PALETTE.inkMute,
                      fontFamily: 'Inter', fontSize: 12, padding: '8px 6px',
                    }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontFamily: 'Fraunces', fontSize: 19, letterSpacing: -0.3, color: PALETTE.ink, lineHeight: 1.2 }}>{b.title}</div>
                  {b.notes && (
                    <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, marginTop: 3, lineHeight: 1.4, wordBreak: 'break-word' }}>{b.notes}</div>
                  )}
                </>
              )}
            </div>
            {editingIdx !== i && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, paddingTop: 2 }}>
                <Tag>{b.tag || 'someday'}</Tag>
                <button onClick={() => startEdit(i)} style={{
                  appearance: 'none', border: 0, background: 'transparent',
                  color: PALETTE.inkMute, cursor: 'pointer', padding: 4,
                }}>
                  <Icon name="pencil" size={14} />
                </button>
                <button onClick={() => deleteItem(i)} style={{
                  appearance: 'none', border: 0, background: 'transparent',
                  color: PALETTE.inkMute, cursor: 'pointer', padding: 4,
                }}>
                  <Icon name="x" size={14} />
                </button>
              </div>
            )}
          </div>
        ))}

        {adding ? (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              autoFocus
              value={newText}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="Where do you want to go?"
              style={{
                appearance: 'none', border: `1px solid ${PALETTE.rule}`,
                borderRadius: 10, padding: '12px 14px', background: PALETTE.card,
                fontFamily: 'Fraunces', fontSize: 16, color: PALETTE.ink, outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>When</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {WHEN_OPTIONS.map(w => (
                  <button key={w} onClick={() => setNewWhen(w)} style={{
                    appearance: 'none', cursor: 'pointer',
                    padding: '4px 10px', borderRadius: 999,
                    background: newWhen === w ? PALETTE.ink : 'transparent',
                    color: newWhen === w ? PALETTE.paper : PALETTE.inkSoft,
                    fontFamily: 'Inter', fontSize: 11, fontWeight: 500,
                    border: newWhen === w ? '1px solid transparent' : `1px solid ${PALETTE.rule}`,
                    transition: 'all 0.12s',
                  }}>{w}</button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={addItem} style={{
                appearance: 'none', border: 0, cursor: 'pointer',
                background: PALETTE.ink, color: PALETTE.paper,
                borderRadius: 10, padding: '10px 20px',
                fontFamily: 'Inter', fontSize: 13, fontWeight: 600,
              }}>Add</button>
              <button onClick={() => { setAdding(false); setNewText(''); setNewWhen('someday'); }} style={{
                appearance: 'none', border: 0, cursor: 'pointer',
                background: 'transparent', color: PALETTE.inkMute,
                fontFamily: 'Inter', fontSize: 13, padding: '10px 8px',
              }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} style={{
            appearance: 'none', border: `1px dashed ${PALETTE.rule}`,
            borderRadius: 12, padding: '14px',
            background: 'transparent', color: PALETTE.inkSoft,
            fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
            marginTop: 16, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            <Icon name="plus" size={16} /> Add to Travel Inspiration
          </button>
        )}
      </div>

      {showIgImport && <IgImportSheet onAdd={handleIgAdd} onClose={() => setShowIgImport(false)} />}
    </>
  );
}

const PACKING_CATS = ['Documents', 'Clothing', 'Electronics', 'Toiletries', 'Health', 'Misc'];

function ProfileView({ isMobile }) {
  const px = isMobile ? 20 : 28;
  const [packingOpen, setPackingOpen] = React.useState(false);
  const [cats, setCats] = React.useState(() => loadDefaultPacking());
  const [addingCat, setAddingCat] = React.useState('Documents');
  const [addingText, setAddingText] = React.useState('');
  const [editingKey, setEditingKey] = React.useState(null); // "catIndex:itemIndex"
  const [editText, setEditText] = React.useState('');

  const persist = (newCats) => { setCats(newCats); saveDefaultPacking(newCats); };

  const addItem = () => {
    if (!addingText.trim()) return;
    const newCats = cats.map(c => c.cat === addingCat ? { ...c, items: [...c.items, addingText.trim()] } : c);
    if (!newCats.find(c => c.cat === addingCat)) {
      newCats.push({ cat: addingCat, items: [addingText.trim()] });
    }
    persist(newCats);
    setAddingText('');
  };

  const removeItem = (catIdx, itemIdx) => {
    const newCats = cats.map((c, ci) => ci === catIdx ? { ...c, items: c.items.filter((_, ii) => ii !== itemIdx) } : c);
    persist(newCats.filter(c => c.items.length > 0));
  };

  const saveEdit = () => {
    if (!editText.trim() || !editingKey) return;
    const [ci, ii] = editingKey.split(':').map(Number);
    const newCats = cats.map((c, cIdx) => cIdx === ci
      ? { ...c, items: c.items.map((item, iIdx) => iIdx === ii ? editText.trim() : item) }
      : c
    );
    persist(newCats);
    setEditingKey(null);
  };

  const resetToDefault = () => { persist(DEFAULT_PACKING); };

  return (
    <div style={{ padding: `20px ${px}px`, color: PALETTE.inkSoft, fontFamily: 'Inter', fontSize: 14 }}>
      <div style={{ fontFamily: 'Fraunces', fontSize: 22, color: PALETTE.ink, marginBottom: 16, fontWeight: 400 }}>Settings</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Packing list defaults */}
        <div style={{ background: PALETTE.card, borderRadius: 12, border: `0.5px solid ${PALETTE.ruleSoft}`, overflow: 'hidden' }}>
          <button
            onClick={() => setPackingOpen(o => !o)}
            style={{
              appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent',
              width: '100%', padding: '16px', textAlign: 'left',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 500, color: PALETTE.ink }}>Default packing list</div>
              <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, marginTop: 3 }}>
                Starting point for every new trip · {cats.reduce((s, c) => s + c.items.length, 0)} items
              </div>
            </div>
            <Icon name={packingOpen ? 'arrowUp' : 'arrowDn'} size={16} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
          </button>

          {packingOpen && (
            <div style={{ padding: '0 16px 16px', borderTop: `0.5px solid ${PALETTE.ruleSoft}` }}>
              {cats.map((cat, ci) => (
                <div key={cat.cat} style={{ marginBottom: 14, marginTop: 14 }}>
                  <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
                    {cat.cat}
                  </div>
                  {cat.items.map((item, ii) => (
                    <div key={ii} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: `0.5px solid ${PALETTE.ruleSoft}` }}>
                      {editingKey === `${ci}:${ii}` ? (
                        <>
                          <input
                            autoFocus value={editText}
                            onChange={e => setEditText(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingKey(null); }}
                            style={{ flex: 1, appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, padding: '4px 8px', fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, background: PALETTE.paper, outline: 'none' }}
                          />
                          <button onClick={saveEdit} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.accent, fontFamily: 'Inter', fontSize: 12, fontWeight: 600 }}>Save</button>
                          <button onClick={() => setEditingKey(null)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, fontFamily: 'Inter', fontSize: 12 }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <span style={{ flex: 1, fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink }}>{item}</span>
                          <button onClick={() => { setEditingKey(`${ci}:${ii}`); setEditText(item); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: '2px 4px' }}>
                            <Icon name="pencil" size={12} />
                          </button>
                          <button onClick={() => removeItem(ci, ii)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: '2px 4px' }}>
                            <Icon name="x" size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {/* Add item */}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <select value={addingCat} onChange={e => setAddingCat(e.target.value)} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '6px 10px', background: PALETTE.paper, width: '100%', fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, marginBottom: 6, outline: 'none' }}>
                    {PACKING_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input
                    value={addingText} onChange={e => setAddingText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addItem()}
                    placeholder="Add item to default list…"
                    style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '8px 10px', width: '100%', background: PALETTE.paper, fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <button onClick={addItem} style={{ appearance: 'none', border: 0, cursor: 'pointer', flexShrink: 0, background: PALETTE.ink, color: PALETTE.paper, borderRadius: 8, padding: '8px 14px', fontFamily: 'Inter', fontSize: 13, fontWeight: 600, height: 38 }}>Add</button>
              </div>

              <button onClick={resetToDefault} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', marginTop: 12, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, textDecoration: 'underline', padding: 0 }}>
                Reset to defaults
              </button>
            </div>
          )}
        </div>

        {[
          { label: 'Preferences', detail: 'Dietary, transport, activity levels' },
          { label: 'Payment methods', detail: 'Add a card for booking' },
          { label: 'Travel documents', detail: 'Passport, visas, loyalty programs' },
          { label: 'Notifications', detail: 'Price alerts, trip reminders' },
        ].map(item => (
          <div key={item.label} style={{
            padding: '16px', background: PALETTE.card, borderRadius: 12,
            border: `0.5px solid ${PALETTE.ruleSoft}`, opacity: 0.65,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 500, color: PALETTE.ink }}>{item.label}</div>
              <span style={{
                fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.5, textTransform: 'uppercase',
                color: PALETTE.inkMute, background: PALETTE.paperDim, border: `0.5px solid ${PALETTE.ruleSoft}`,
                borderRadius: 999, padding: '2px 8px',
              }}>Coming soon</span>
            </div>
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, marginTop: 3 }}>{item.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
